import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import axios from "axios";

const StockDetails = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);

  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract query params
  const branchId = searchParams.get("branchid");
  const itemId = searchParams.get("itemid");
  const fromDate = searchParams.get("fromdate");
  const toDate = searchParams.get("todate");

  useEffect(() => {
    if (branchId && itemId) {
      fetchStockDetails();
    } else {
      setLoading(false);
      setError("Branch and item are required");
    }
  }, [branchId, itemId, fromDate, toDate]);

  const fetchStockDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "https://nissi-india.com/api/stock_summaryViewRpt",
        {
          headers: {
            "x-finyearid": localStorage.getItem("finYearId") || "",
            "x-finyear": localStorage.getItem("finYear") || "",
            "x-branchid": localStorage.getItem("branchId") || "",
            "x-branch": localStorage.getItem("branchType") || "",
            "x-userid": localStorage.getItem("userId") || "",
          },
          params: {
            stockBranchId: branchId,
            stockItemId: itemId,
            stockFromDate: fromDate,
            stockToDate: toDate,
          },
        }
      );
      setData(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error("Fetch failed", err);
      setError("Failed to load stock details");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="INDIVIDUAL STOCK SUMMARY" />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-black"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {loading ? (
          <div className="text-center">Loading...</div>
        ) : error ? (
          <div className="text-center text-red-500">{error}</div>
        ) : data.length === 0 ? (
          <div className="text-center text-gray-500">No Records Found!</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border text-sm">
              <thead>
                <tr className="bg-blue-900 text-white text-center">
                  <th rowSpan="2" className="border px-2 py-1">Sl No</th>
                  <th rowSpan="2" className="border px-2 py-1">Branch</th>
                  <th rowSpan="2" className="border px-2 py-1">Item Code</th>
                  <th rowSpan="2" className="border px-2 py-1">Item Name</th>
                  <th rowSpan="2" className="border px-2 py-1">UOM</th>

                  {/* Group headers */}
                  <th colSpan="3" className="border px-2 py-1">Opening Stock</th>
                  <th colSpan="3" className="border px-2 py-1">GRN</th>
                  <th colSpan="3" className="border px-2 py-1">STNI</th>
                  <th colSpan="3" className="border px-2 py-1">SR</th>
                  <th colSpan="3" className="border px-2 py-1">SI</th>
                  <th colSpan="3" className="border px-2 py-1">STNO</th>
                  <th colSpan="3" className="border px-2 py-1">Closing Stock</th>
                </tr>
                <tr className="bg-blue-700 text-white text-center">
                  {Array(7).fill(null).map((_, idx) => (
                    <React.Fragment key={idx}>
                      <th className="border px-2 py-1">Qty</th>
                      <th className="border px-2 py-1">Rate</th>
                      <th className="border px-2 py-1">Value</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={index} className="text-center">
                    <td className="border px-2 py-1">{index + 1}</td>
                                    <td className="border px-2 py-1">{item.fld_branchname}</td>
                    <td className="border px-2 py-1">{item.fld_itemcode}</td>
                    <td className="border px-2 py-1">{item.fld_itemname}</td>
                    <td className="border px-2 py-1">{item.fld_uomname}</td>

                    {/* Opening Stock */}
                    <td className="border px-2 py-1">{item.opqty1}</td>
                    <td className="border px-2 py-1">{item.opprice1}</td>
                    <td className="border px-2 py-1">{item.opamount1}</td>

                    {/* GRN */}
                    <td className="border px-2 py-1">{item.grnqty}</td>
                    <td className="border px-2 py-1">{item.grnprice}</td>
                    <td className="border px-2 py-1">{item.grnamount}</td>

                    {/* STNI */}
                    <td className="border px-2 py-1">{item.stniqty}</td>
                    <td className="border px-2 py-1">{item.stniprice}</td>
                    <td className="border px-2 py-1">{item.stniamount}</td>

                    {/* SR */}
                    <td className="border px-2 py-1">{item.srqty}</td>
                    <td className="border px-2 py-1">{item.srprice}</td>
                    <td className="border px-2 py-1">{item.sramount}</td>

                    {/* SI */}
                    <td className="border px-2 py-1">{item.siqty}</td>
                    <td className="border px-2 py-1">{item.siprice}</td>
                    <td className="border px-2 py-1">{item.siamount}</td>

                    {/* STNO */}
                    <td className="border px-2 py-1">{item.stnoqty}</td>
                    <td className="border px-2 py-1">{item.stnoprice}</td>
                    <td className="border px-2 py-1">{item.stnoamount}</td>

                    {/* Closing */}
                    <td className="border px-2 py-1">{item.closingQty}</td>
                    <td className="border px-2 py-1">{item.closingprice}</td>
                    <td className="border px-2 py-1">{item.closingvalue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default StockDetails;
